import axios, { AxiosResponse } from 'axios';

// API 기본 설정 - 환경에 따라 다른 URL 사용
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'https://prod-af-social-tracker.alpha-filter.xyz'
  : 'https://prod-af-social-tracker.alpha-filter.xyz'

// axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 1000000, // 1000초로 연장
  headers: {
    'Content-Type': 'application/json',
  },
});

// 응답 인터셉터 - 에러 처리
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    throw error;
  }
);

export enum TweetSyncStatus {
  ACTIVE = 'ACTIVE',
  INIT_SYNC = 'INIT_SYNC',
  SYNCING = 'SYNCING',
  STOPPED = 'STOPPED',
  ERROR = 'ERROR',
}


// 타입 정의
export interface CommunityHandle {
  handle: string;
  role: 'ADMIN' | 'MOD';
}

export interface CreateTweetSyncDto {
  tokenAddress: string;
  tokenSymbol: string;
  communityHandles?: Array<{ handle: string; role: 'ADMIN' | 'MOD' }>;
  communityId?: string;
  includeSymbolInQuery?: boolean;
  associatedHandles?: string[];
}

export interface ResumeSyncDto {
  communityHandles?: Array<{ handle: string; role: 'ADMIN' | 'MOD' }>;
  communityId?: string;
  includeSymbolInQuery?: boolean;
  associatedHandles?: string[];
}

export interface PaginationDto {
  page?: number;
  limit?: number;
}

export interface BatchCreateAuthorsDto {
  userIds: string[];
  label?: string;
}

export interface BatchCreateAuthorsByUsernameDto {
  userNames: string[];
  label?: string;
  delayMs?: number;
}

export interface BulkTokenSyncStatisticsItem {
  tokenAddress: string;
  tweetSyncId: string;
  totalTweets: number;
  tweetsInTwoHoursAgo: number;
  tweetsInTwoHoursAgoDeltaAbs: number;
  tweetsInTwoHoursAgoDeltaPct: number | null;
  tweetsInLastHour: number;
  tweetsInLastHourDeltaAbs: number;
  tweetsInLastHourDeltaPct: number | null;
  totalAdminTweets: number;
  adminTweetsInTwoHoursAgo: number;
  adminTweetsInTwoHoursAgoDeltaAbs: number;
  adminTweetsInTwoHoursAgoDeltaPct: number | null;
  adminTweetsInLastHour: number;
  adminTweetsInLastHourDeltaAbs: number;
  adminTweetsInLastHourDeltaPct: number | null;
  totalModTweets: number;
  modTweetsInTwoHoursAgo: number;
  modTweetsInTwoHoursAgoDeltaAbs: number;
  modTweetsInTwoHoursAgoDeltaPct: number | null;
  modTweetsInLastHour: number;
  modTweetsInLastHourDeltaAbs: number;
  modTweetsInLastHourDeltaPct: number | null;
  totalHighFollowerTweets: number;
  highFollowerTweetsInTwoHoursAgo: number;
  highFollowerTweetsInTwoHoursAgoDeltaAbs: number;
  highFollowerTweetsInTwoHoursAgoDeltaPct: number | null;
  highFollowerTweetsInLastHour: number;
  highFollowerTweetsInLastHourDeltaAbs: number;
  highFollowerTweetsInLastHourDeltaPct: number | null;
  uniqueHighFollowerAuthors: number;
  uniqueHighFollowerAuthorsInTwoHoursAgo: number;
  uniqueHighFollowerAuthorsInTwoHoursAgoDeltaAbs: number;
  uniqueHighFollowerAuthorsInTwoHoursAgoDeltaPct: number | null;
  uniqueHighFollowerAuthorsInLastHour: number;
  uniqueHighFollowerAuthorsInLastHourDeltaAbs: number;
  uniqueHighFollowerAuthorsInLastHourDeltaPct: number | null;
  /** max(communitySyncedTimeRanges.untilTime), epoch ms; none synced yet => null */
  lastCommunityCollectedUntilMs: number | null;
}

export interface TweetSyncResponse {
  _id: string;
  tokenAddress: string;
  tokenSymbol: string;
  communityHandles: Array<{ handle: string; role: 'ADMIN' | 'MOD' }>;
  associatedHandles?: string[];
  description: string;
  communityId?: string | null;
  status: TweetSyncStatus;
  createdAt: string;
  updatedAt: string;
  creditUsed?: number;
  totalTweetsCollected?: number;
  lastExecutedAt?: string;
  tweetSyncId?: string;
  syncedTimeRanges?: Array<{ sinceTime: number; untilTime: number }>;
  communityLastSyncedTime?: number | null;
}

export interface Tweet {
  id: string;
  text: string;
  authorId: string;
  createdAt: string;
  tweetSyncId: string;
  communityId?: string | null;
  hashtags?: string[];
  symbols?: string[];
  // 트윗 통계 정보 (선택적)
  viewCount?: number;
  likeCount?: number;
  bookmarkCount?: number;
  retweetCount?: number;
  quoteCount?: number;
  replyCount?: number;
  author: {
    id: string;
    name: string;
    userName: string;
    label: string;
    isVerified: boolean;
    isBlueVerified: boolean;
    verifiedType: string;
    profilePicture: string;
    coverPicture: string;
    location: string;
    followers: number;
    following: number;
    createdAt: string;
  };
}

export interface Author {
  id: string;
  userName: string;
  displayName?: string;
  name?: string | null;
  label?: string;
  createdAt?: string;
  updatedAt?: string;
  profilePicture?: string | null;
  followers?: number;
  following?: number;
  isBlacklisted?: boolean;
  isVerified?: boolean;
  isBlueVerified?: boolean;
  tweetId?: string;
  tweetCount?: number;
  description?: string | null;
  url?: string | null;
}

export interface PinnedTweetResponse {
  success: boolean;
  data?: {
    pinnedTweetId?: string | null;
  };
}

export interface TweetStatsByIdResponse {
  success: boolean;
  data?: {
    likes: number;
    views: number;
    retweets: number;
    replies: number;
    quotes: number;
    bookmarks: number;
  } | null;
}

export interface AuthorDetailResponse {
  message?: string;
  data?: Author | null;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

export interface TweetSyncSourceStatusItem {
  sourceType: string;
  sourceKey: string | null;
  status: string;
}

export interface AccountInfo {
  recharge_credits: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface FrequentlyQuotedTweetsResponse {
  success: boolean;
  message: string;
  data: Tweet[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  filters: {
    syncId: string;
    minQuoteCount: number;
  };
}

// 엔티티 통계 응답 타입
export interface EntityStatisticsResponse {
  hashtags: Array<{
    entityType: string;
    entityValue: string;
    mentionCount: number;
  }>;
  urls: Array<{
    entityType: string;
    entityValue: string;
    mentionCount: number;
  }>;
  user_mentions: Array<{
    entityType: string;
    entityValue: string;
    mentionCount: number;
    authorInfo?: {
      id?: string;
      name?: string;
      userName?: string;
      profilePicture?: string;
      label?: string;
      followers?: number;
    };
  }>;
}

// 트윗 분석 응답 타입
export interface TweetAnalyticsData {
  timeSlot: string; // YYYY-MM-DDTHH:mm:00.000Z 형식
  tweetCount: number;
  cumulativeCount: number;
  uniqueAuthorCount: number; // 해당 분의 unique author 수
  cumulativeUniqueAuthorCount: number; // 누적 unique author 수
  communityUniqueAuthorCount: number; // 해당 분의 커뮤니티 트윗 unique author 수
  cumulativeCommunityUniqueAuthorCount: number; // 누적 커뮤니티 트윗 unique author 수
  // 분당 분석용 필드
  labeledAuthorCount: number; // label 있는 author 트윗 수
  highFollowerCount: number; // 팔로워 50,000 이상 author 트윗 수 (label 없는 경우만)
  communityTweetCount: number; // 커뮤니티 트윗 수 (labeledAuthor나 highFollower와 독립적)
  otherCount: number; // 그 이외 모든 author 트윗 수
  // 트윗 ID 리스트 (ID와 생성일시 포함)
  labeledAuthorTweetIds: Array<{ id: string; createdAtDate: string }>; // label 있는 author 트윗 ID 리스트
  highFollowerTweetIds: Array<{ id: string; createdAtDate: string }>; // 팔로워 50,000 이상 author 트윗 ID 리스트
  communityTweetIds: Array<{ id: string; createdAtDate: string }>; // 커뮤니티 트윗 ID 리스트
  otherTweetIds: Array<{ id: string; createdAtDate: string }>; // 그 이외 모든 author 트윗 ID 리스트
}

export interface AuthorInfo {
  id: string;
  name: string;
  userName: string;
  label?: string;
  followers?: number;
  tweetCount: number;
  profileLink: string;
}

// 백테스트 통계 타입 정의
export interface CorrelationCoefficient {
  windowValue: number | null; // 현재 윈도우 값 시리즈와 MC의 상관계수
  cumulativeValue: number | null; // 누적 값 시리즈와 MC의 상관계수
}

export interface BacktestStatisticsResponse {
  windows: WindowStatistics[];
  kolAuthors: Author[];
  highFollowerAuthors: Author[];
}

export interface Slope {
  slope: number | null;
}

export interface WindowStatistics {
  windowStart: string;
  windowEnd: string;
  closePrice: number | null; // 해당 윈도우 종료 시점의 종가 (OHLCV)
  post: {
    totalTweetsInWindow: number;
    communityTweetsInWindow: number;
    cumulativeTotalTweets: number;
    cumulativeCommunityTweets: number;

    correlation?: CorrelationCoefficient; // totalTweetsInWindow / cumulativeTotalTweets
    communityCorrelation?: CorrelationCoefficient; // communityTweetsInWindow / cumulativeCommunityTweets
  };
  author: {
    cumulativeUniqueAuthors: number;
    cumulativeUniqueCommunityAuthors: number;
    uniqueAuthorsInWindow: number;
    uniqueCommunityAuthorsInWindow: number;
    windowSeenAuthors: number;
    cumulativeSeenAuthors: number;
    windowSeenCommunityAuthors: number;
    cumulativeSeenCommunityAuthors: number;
    uniqueAuthorsSlope?: Slope;
    uniqueCommunityAuthorsSlope?: Slope;
    correlation?: CorrelationCoefficient; // uniqueAuthorsInWindow / cumulativeUniqueAuthors
    communityCorrelation?: CorrelationCoefficient; // uniqueCommunityAuthorsInWindow / cumulativeUniqueCommunityAuthors
    seenAuthorsCorrelation?: CorrelationCoefficient; // windowSeenAuthors / cumulativeSeenAuthors
    seenCommunityAuthorsCorrelation?: CorrelationCoefficient; // windowSeenCommunityAuthors / cumulativeSeenCommunityAuthors
  };
  communityLeader: {
    cumulativeAdminTweets: number;
    adminTweetsInWindow: number;
    cumulativeModTweets: number;
    modTweetsInWindow: number;
    cumulativeModAvgTweetsPerPerson: number;
    modAvgTweetsPerPersonInWindow: number;
    adminTweetsSlope?: Slope;
    modTweetsSlope?: Slope;
    correlation?: CorrelationCoefficient; // adminTweetsInWindow / cumulativeAdminTweets
    modCorrelation?: CorrelationCoefficient; // modTweetsInWindow / cumulativeModTweets
    modAvgCorrelation?: CorrelationCoefficient; // modAvgTweetsPerPersonInWindow / cumulativeModAvgTweetsPerPerson
  };
  highQualityMentionHighFollower: {
    cumulativeUniqueAuthors: number;
    uniqueAuthorsInWindow: number;
    cumulativeTotalFollowers: number;
    totalFollowersInWindow: number;
    cumulativeTotalTweets: number;
    totalTweetsInWindow: number;
    cumulativeAvgTweetsPerPerson: number;
    avgTweetsPerPersonInWindow: number;

    correlation?: CorrelationCoefficient; // totalTweetsInWindow / cumulativeTotalTweets
    uniqueAuthorsCorrelation?: CorrelationCoefficient; // uniqueAuthorsInWindow / cumulativeUniqueAuthors
    followersCorrelation?: CorrelationCoefficient; // totalFollowersInWindow / cumulativeTotalFollowers
    avgCorrelation?: CorrelationCoefficient; // avgTweetsPerPersonInWindow / cumulativeAvgTweetsPerPerson
  };
  highQualityMentionKoL: {
    cumulativeUniqueAuthors: number;
    uniqueAuthorsInWindow: number;
    cumulativeTotalFollowers: number;
    totalFollowersInWindow: number;
    cumulativeTotalTweets: number;
    totalTweetsInWindow: number;
    cumulativeAvgTweetsPerPerson: number;
    avgTweetsPerPersonInWindow: number;

    correlation?: CorrelationCoefficient; // totalTweetsInWindow / cumulativeTotalTweets
    uniqueAuthorsCorrelation?: CorrelationCoefficient; // uniqueAuthorsInWindow / cumulativeUniqueAuthors
    followersCorrelation?: CorrelationCoefficient; // totalFollowersInWindow / cumulativeTotalFollowers
    avgCorrelation?: CorrelationCoefficient; // avgTweetsPerPersonInWindow / cumulativeAvgTweetsPerPerson
  };
}

export interface WindowTweet {
  id: string;
  text: string;
  createdAt: string;
  createdAtDate: string;
  author: {
    id: string;
    userName: string;
    name: string | null;
    profilePicture: string | null;
    followers: number;
    label: string | null;
    isBlacklisted: boolean;
  };
  retweetCount: number;
  replyCount: number;
  likeCount: number;
  quoteCount: number;
  viewCount: number;
  bookmarkCount: number;
  quotedTweet?: WindowTweet | null;
  url?: string;
}

export interface WindowAuthor {
  id: string;
  userName: string;
  name: string | null;
  profilePicture: string | null;
  followers: number;
  label: string | null;
  isBlacklisted: boolean;
}

export interface WindowTweetsAuthorsResponse {
  windowStart: string;
  windowEnd: string;
  tweets: WindowTweet[];
  uniqueAuthors: WindowAuthor[];
}

export interface BySourceTweetsData {
  tweetSyncId: string;
  syncMeta: TweetSyncResponse;
  tweets: {
    adminTweets: WindowTweet[];
    modTweets: WindowTweet[];
    highFollowerTweets: WindowTweet[];
    otherTweets: WindowTweet[];
    /** X_TWEET sync: replies to the monitored tweet id (`sourceKey`). */
    replyTweets?: WindowTweet[];
    /** X_TWEET sync: quote tweets of the monitored tweet. */
    quoteTweets?: WindowTweet[];
    /** X_ACCOUNT sync: posts authored by the monitored account (timeline). */
    feedTweets?: WindowTweet[];
  };
  totalTweets: number;
}

export interface BySourceResponse {
  success: boolean;
  message?: string;
  data: BySourceTweetsData | null;
}

export interface HourlyAdminModCumulativeResponse {
  tweetSyncId: string;
  anchorTimestampMs: number;
  windowHours: number;
  adminCumulativeTotals: number[];
  modCumulativeTotals: number[];
}


// API 함수들
export const api = {
  // ===== 트윗 싱크 관리 =====
  
  /**
   * 트윗 싱크 목록을 조회합니다.
   */
  async getTweetSyncs(pagination: PaginationDto = { limit: 50, page: 1 }): Promise<ApiResponse<TweetSyncResponse[]>> {
    try {
      const response: AxiosResponse<ApiResponse<TweetSyncResponse[]>> = await apiClient.get(
        '/tweet-sync',
        { params: pagination }
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync 에러:', error);
      throw new Error(`트윗 싱크 목록 조회 실패: ${error}`);
    }
  },

  /**
   * 심볼 또는 토큰 주소로 트윗 싱크를 검색합니다.
   */
  async searchTweetSyncs(query: string): Promise<ApiResponse<TweetSyncResponse[]>> {
    try {
      const response: AxiosResponse<ApiResponse<TweetSyncResponse[]>> = await apiClient.get(
        '/tweet-sync/search',
        { params: { query } }
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/search 에러:', error);
      throw new Error(`트윗 싱크 검색 실패: ${error}`);
    }
  },
  
  /**
   * 트윗 싱크를 생성합니다.
   */
  async createTweetSync(dto: CreateTweetSyncDto): Promise<TweetSyncResponse> {
    try {
      const response: AxiosResponse<TweetSyncResponse> = await apiClient.post(
        '/tweet-sync',
        dto
      );
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync 에러:', error);
      throw new Error(`트윗 싱크 생성 실패: ${error}`);
    }
  },

  /**
   * 특정 트윗 싱크를 조회합니다.
   */
  async getTweetSync(tokenAddress: string): Promise<TweetSyncResponse> {
    try {
      const response: AxiosResponse<TweetSyncResponse> = await apiClient.get(
        `/tweet-sync/${tokenAddress}`
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/{id} 에러:', error);
      throw new Error(`트윗 싱크 조회 실패: ${error}`);
    }
  },

  async getTweetSyncBySource(
    tokenAddress: string,
    sourceType: string,
    sourceKey: string,
  ): Promise<BySourceResponse> {
    try {
      const response: AxiosResponse<BySourceResponse> = await apiClient.get(
        `/tweet-sync/${tokenAddress}/by-source`,
        { params: { sourceType, sourceKey } },
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/{tokenAddress}/by-source 에러:', error);
      return { success: false, data: null };
    }
  },

  /**
   * 특정 토큰 주소에 대한 source별 sync 상태를 조회합니다.
   */
  async getTweetSyncSourceStatuses(tokenAddress: string): Promise<TweetSyncSourceStatusItem[]> {
    try {
      const response: AxiosResponse<{
        success: boolean;
        statuses?: TweetSyncSourceStatusItem[];
      }> = await apiClient.get(`/tweet-sync/${tokenAddress}/statuses`);
      return response.data.statuses ?? [];
    } catch (error) {
      console.error('❌ GET /tweet-sync/{tokenAddress}/statuses 에러:', error);
      throw new Error(`source별 트윗 싱크 상태 조회 실패: ${error}`);
    }
  },

  /**
   * 여러 토큰 주소에 대한 트윗 싱크 집계 통계를 한 번에 조회합니다.
   */
  async getBulkTokenSyncStatistics(
    tokenAddresses: string[],
  ): Promise<ApiResponse<BulkTokenSyncStatisticsItem[]>> {
    try {
      const response: AxiosResponse<ApiResponse<BulkTokenSyncStatisticsItem[]>> =
        await apiClient.post('/tweet-sync/bulk-token-sync-statistics', {
          tokenAddresses,
        });
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync/bulk-token-sync-statistics 에러:', error);
      throw new Error(`벌크 토큰 싱크 통계 조회 실패: ${error}`);
    }
  },

  /**
   * 트윗 싱크를 재시작합니다.
   */
  async resumeTweetSync(id: string, dto?: ResumeSyncDto): Promise<{ message: string; sync: TweetSyncResponse }> {
    try {
      const response: AxiosResponse<{ message: string; sync: TweetSyncResponse }> = await apiClient.post(
        `/tweet-sync/${id}/resume`,
        dto || {}
      );
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync/{id}/resume 에러:', error);
      throw new Error(`트윗 싱크 재시작 실패: ${error}`);
    }
  },

  /**
   * 트윗 싱크를 중지합니다.
   */
  async stopTweetSync(id: string): Promise<{ message: string; sync: TweetSyncResponse }> {
    try {
      const response: AxiosResponse<{ message: string; sync: TweetSyncResponse }> = await apiClient.post(
        `/tweet-sync/${id}/stop`
      );
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync/{id}/stop 에러:', error);
      throw new Error(`트윗 싱크 중지 실패: ${error}`);
    }
  },

  /**
   * 모든 트윗 싱크를 중지합니다.
   */
  async stopAllTweetSyncs(): Promise<{ message: string; stoppedCount: number }> {
    try {
      const response: AxiosResponse<{ message: string; stoppedCount: number }> = await apiClient.post(
        '/tweet-sync/stop-all'
      );
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync/stop-all 에러:', error);
      throw new Error(`트윗 싱크 전체 중지 실패: ${error}`);
    }
  },

  /**
   * 트윗 싱크를 삭제합니다.
   */
  async deleteTweetSync(id: string): Promise<{ message: string; deleted: boolean }> {
    try {
      const response: AxiosResponse<{ message: string; deleted: boolean }> = await apiClient.delete(
        `/tweet-sync/${id}`
      );
      return response.data;
    } catch (error) {
      console.error('❌ DELETE /tweet-sync/{id} 에러:', error);
      throw new Error(`트윗 싱크 삭제 실패: ${error}`);
    }
  },

  // ===== 트윗 조회 =====

  /**
   * 특정 작성자의 트윗을 조회합니다.
   */
  async getTweetsByAuthor(authorId: string, pagination: PaginationDto = {}): Promise<PaginatedResponse<Tweet>> {
    try {
      const response: AxiosResponse<PaginatedResponse<Tweet>> = await apiClient.get(
        `/tweet-sync/tweets/author/${authorId}`,
        { params: pagination }
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/tweets/author/{authorId} 에러:', error);
      throw new Error(`작성자별 트윗 조회 실패: ${error}`);
    }
  },

  /**
   * 특정 Tweet-sync를 통해 가져온 특정 작성자의 트윗을 조회합니다.
   */
  async getTweetsByAuthorInSync(syncId: string, authorId: string, pagination: PaginationDto = {}): Promise<PaginatedResponse<Tweet>> {
    try {
      const response: AxiosResponse<PaginatedResponse<Tweet>> = await apiClient.get(
        `/tweet-sync/${syncId}/tweets/author/${authorId}`,
        { params: pagination }
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/{syncId}/tweets/author/{authorId} 에러:', error);
      throw new Error(`특정 싱크의 작성자별 트윗 조회 실패: ${error}`);
    }
  },

  /**
   * 해시태그로 트윗을 조회합니다.
   */
  async getTweetsByHashtags(hashtags: string[], pagination: PaginationDto = {}): Promise<PaginatedResponse<Tweet>> {
    try {
      const response: AxiosResponse<PaginatedResponse<Tweet>> = await apiClient.get(
        '/tweet-sync/tweets/hashtags',
        { 
          params: { 
            hashtags: hashtags.join(','),
            ...pagination 
          } 
        }
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/tweets/hashtags 에러:', error);
      throw new Error(`해시태그별 트윗 조회 실패: ${error}`);
    }
  },

  /**
   * 심볼로 트윗을 조회합니다.
   */
  async getTweetsBySymbols(symbols: string[], pagination: PaginationDto = {}): Promise<PaginatedResponse<Tweet>> {
    try {
      const response: AxiosResponse<PaginatedResponse<Tweet>> = await apiClient.get(
        '/tweet-sync/tweets/symbols',
        { 
          params: { 
            symbols: symbols.join(','),
            ...pagination 
          } 
        }
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/tweets/symbols 에러:', error);
      throw new Error(`심볼별 트윗 조회 실패: ${error}`);
    }
  },

  /**
   * 최근 트윗을 조회합니다.
   */
  async getRecentTweets(pagination: PaginationDto = {}): Promise<PaginatedResponse<Tweet>> {
    try {
      const response: AxiosResponse<PaginatedResponse<Tweet>> = await apiClient.get(
        '/tweet-sync/tweets/recent',
        { params: pagination }
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/tweets/recent 에러:', error);
      throw new Error(`최근 트윗 조회 실패: ${error}`);
    }
  },

  /**
   * 특정 트윗을 조회합니다.
   */
  async getTweetById(tweetId: string): Promise<Tweet> {
    try {
      const response: AxiosResponse<Tweet> = await apiClient.get(
        `/tweet-sync/tweets/${tweetId}`
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/tweets/{tweetId} 에러:', error);
      throw new Error(`트윗 조회 실패: ${error}`);
    }
  },

  /**
   * 계정의 pinned tweet id를 조회합니다.
   */
  async getPinnedTweetId(handle: string): Promise<string | null> {
    try {
      const response: AxiosResponse<PinnedTweetResponse> = await apiClient.get(
        `/tweet-sync/pinned-tweet/${encodeURIComponent(handle)}`
      );
      return response.data?.data?.pinnedTweetId ?? null;
    } catch (error) {
      console.error('❌ GET /tweet-sync/pinned-tweet/{handle} 에러:', error);
      throw new Error(`Pinned tweet 조회 실패: ${error}`);
    }
  },

  async getTweetStatsById(tweetId: string): Promise<TweetStatsByIdResponse['data'] | null> {
    try {
      const response: AxiosResponse<TweetStatsByIdResponse> = await apiClient.get(
        `/tweet-sync/tweet-stats/${encodeURIComponent(tweetId)}`
      );
      return response.data?.data ?? null;
    } catch (error) {
      console.error('❌ GET /tweet-sync/tweet-stats/{tweetId} 에러:', error);
      throw new Error(`트윗 통계 조회 실패: ${error}`);
    }
  },

  async getAuthorDetailByUsername(userName: string): Promise<Author | null> {
    try {
      const response: AxiosResponse<AuthorDetailResponse> = await apiClient.get(
        `/tweet-sync/authors/${encodeURIComponent(userName)}/detail`
      );
      return response.data?.data ?? null;
    } catch (error) {
      console.error('❌ GET /tweet-sync/authors/{userName}/detail 에러:', error);
      throw new Error(`Author 상세 조회 실패: ${error}`);
    }
  },

  /**
   * 특정 트윗 싱크로부터 수집된 트윗을 커뮤니티 필터링과 함께 조회합니다.
   */
  async getTweetsBySyncId(
    tokenAddress: string, 
    communityOnly: boolean = false, 
    pagination: PaginationDto = {}
  ): Promise<PaginatedResponse<Tweet>> {
    try {
      const response: AxiosResponse<PaginatedResponse<Tweet>> = await apiClient.get(
        `/tweet-sync/${tokenAddress}/tweets`,
        { 
          params: {
            ...pagination,
            communityOnly
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error(`❌ GET /tweet-sync/${tokenAddress}/tweets 에러:`, error);
      throw new Error(`싱크별 필터링된 트윗 조회 실패: ${error}`);
    }
  },

  // ===== 작성자 관리 =====

  /**
   * 사용자 ID로 작성자를 배치 생성합니다.
   */
  async batchCreateAuthors(dto: BatchCreateAuthorsDto): Promise<ApiResponse<Author[]>> {
    try {
      const response: AxiosResponse<ApiResponse<Author[]>> = await apiClient.post(
        '/tweet-sync/authors/batch',
        dto
      );
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync/authors/batch 에러:', error);
      throw new Error(`작성자 배치 생성 실패: ${error}`);
    }
  },

  /**
   * 사용자명으로 작성자를 배치 생성합니다.
   */
  async batchCreateAuthorsByUsername(dto: BatchCreateAuthorsByUsernameDto): Promise<ApiResponse<Author[]>> {
    try {
      const response: AxiosResponse<ApiResponse<Author[]>> = await apiClient.post(
        '/tweet-sync/authors/batch-by-username',
        dto
      );
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync/authors/batch-by-username 에러:', error);
      throw new Error(`사용자명으로 작성자 배치 생성 실패: ${error}`);
    }
  },

  /**
   * 작성자의 라벨을 업데이트합니다.
   */
  async updateAuthorLabel(authorId: string, label: string): Promise<ApiResponse<Author>> {
    try {
      const response: AxiosResponse<ApiResponse<Author>> = await apiClient.post(
        `/tweet-sync/authors/${authorId}/label`,
        { label }
      );
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync/authors/{authorId}/label 에러:', error);
      throw new Error(`작성자 라벨 업데이트 실패: ${error}`);
    }
  },

  /**
   * 작성자의 라벨을 제거합니다.
   */
  async removeAuthorLabel(authorId: string): Promise<ApiResponse<Author>> {
    try {
      const response: AxiosResponse<ApiResponse<Author>> = await apiClient.delete(
        `/tweet-sync/authors/${authorId}/label`
      );
      return response.data;
    } catch (error) {
      console.error('❌ DELETE /tweet-sync/authors/{authorId}/label 에러:', error);
      throw new Error(`작성자 라벨 제거 실패: ${error}`);
    }
  },

  /**
   * 라벨로 작성자를 조회합니다.
   */
  async getAuthorsByLabel(label: string): Promise<ApiResponse<Author[]>> {
    try {
      const response: AxiosResponse<ApiResponse<Author[]>> = await apiClient.get(
        `/tweet-sync/authors/labels/${label}`
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/authors/labels/{label} 에러:', error);
      throw new Error(`라벨별 작성자 조회 실패: ${error}`);
    }
  },

  /**
   * 모든 라벨을 조회합니다.
   */
  async getAllAuthorLabels(): Promise<ApiResponse<string[]>> {
    try {
      const response: AxiosResponse<ApiResponse<string[]>> = await apiClient.get(
        '/tweet-sync/authors/labels'
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/authors/labels 에러:', error);
      throw new Error(`모든 라벨 조회 실패: ${error}`);
    }
  },

  // ===== 계정 정보 =====

  /**
   * 내 계정 정보를 조회합니다.
   */
  async getMyAccountInfo(): Promise<ApiResponse<AccountInfo>> {
    try {
      const response: AxiosResponse<ApiResponse<AccountInfo>> = await apiClient.get(
        '/tweet-sync/account/info'
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/account/info 에러:', error);
      throw new Error(`계정 정보 조회 실패: ${error}`);
    }
  },

  // ===== 자주 Quote된 트윗 조회 =====

  /**
   * 특정 싱크로부터 수집된 트윗들 중 3번 이상 quote된 트윗들을 최신순으로 조회합니다.
   * @param syncId 트윗 싱크 ID
   * @param pagination 페이지네이션 옵션
   * @returns 자주 quote된 트윗 목록
   */
  async getFrequentlyQuotedTweetsByTokenAddress(
    tokenAddress: string,
    pagination: PaginationDto = { page: 1, limit: 20 },
    minQuoteCount: number = 3,
  ): Promise<FrequentlyQuotedTweetsResponse> {
    try {
      const response: AxiosResponse<FrequentlyQuotedTweetsResponse> = await apiClient.get(
        `/tweet-sync/${tokenAddress}/frequently-quoted`,
        {
          params: {
            ...pagination,
            minQuoteCount,
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/{tokenAddress}/frequently-quoted 에러:', error);
      throw new Error(`자주 quote된 트윗 조회 실패: ${error}`);
    }
  },

  // ===== 엔티티 통계 조회 =====

  /**
   * 특정 싱크의 엔티티 통계를 조회합니다 (자주 언급된 링크, 해시태그 등).
   * @param syncId 트윗 싱크 ID
   * @param minCount 최소 언급 횟수 (기본값: 10)
   * @returns 엔티티 통계 데이터
   */
  async getEntityStatisticsByTokenAddress(
    tokenAddress: string,
    minCount: number = 5,
  ): Promise<ApiResponse<EntityStatisticsResponse>> {
    try {
      const response: AxiosResponse<ApiResponse<EntityStatisticsResponse>> = await apiClient.get(
        `/tweet-sync/${tokenAddress}/entity-statistics`,
        {
          params: {
            minCount,
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/{tokenAddress}/entity-statistics 에러:', error);
      throw new Error(`엔티티 통계 조회 실패: ${error}`);
    }
  },

  // ===== 트윗 분석 조회 =====

  /**
   * 특정 싱크의 트윗 분석 데이터를 조회합니다 (1분당 누적량, 모든 카테고리 포함).
   * @param syncId 트윗 싱크 ID
   * @returns 트윗 분석 데이터 (모든 카테고리 포함)
   */
  async getTweetAnalyticsByTokenAddress(tokenAddress: string): Promise<{
    success: boolean;
    message: string;
    data: {
      analytics: TweetAnalyticsData[];
      authors: Record<string, AuthorInfo>;
    };
  }> {
    try {
      const response: AxiosResponse<{
        success: boolean;
        message: string;
        data: {
          analytics: Array<TweetAnalyticsData>;
          authors: Record<string, AuthorInfo>;
        };
      }> = await apiClient.get(`/tweet-sync/${tokenAddress}/analytics`);
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/{syncId}/analytics 에러:', error);
      throw new Error(`트윗 분석 데이터 조회 실패: ${error}`);
    }
  },

  // ===== 트윗 ID 리스트로 조회 =====

  /**
   * 트윗 ID 리스트로 트윗들을 조회합니다.
   * @param tweetIds 트윗 ID 배열
   * @returns 트윗 목록
   */
  async getTweetsByIds(tweetIds: string[]): Promise<PaginatedResponse<Tweet>> {
    try {
      const response: AxiosResponse<{
        data: Tweet[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
      }> = await apiClient.post(
        '/tweet-sync/tweets/by-ids',
        { tweetIds }
      );
      // 응답 형식을 PaginatedResponse로 변환
      return {
        data: response.data.data,
        total: response.data.pagination.total,
        page: response.data.pagination.page,
        limit: response.data.pagination.limit,
        totalPages: response.data.pagination.totalPages,
      };
    } catch (error) {
      console.error('❌ POST /tweet-sync/tweets/by-ids 에러:', error);
      throw new Error(`Tweet ID list lookup failed: ${error}`);
    }
  },

  // ===== 백테스트 통계 =====

  /**
   * 특정 싱크에서 지정한 시간 범위 트윗 JSON 다운로드
   * @param tokenAddress 토큰 컨트랙트 주소
   * @param startDate ISO 형식 시작일
   * @param endDate ISO 형식 종료일
   */
  async downloadSyncTweets(
    tokenAddress: string,
    startDate: string,
    endDate: string,
  ): Promise<void> {
    try {
      const response: AxiosResponse<{ data: WindowTweet[] }> = await apiClient.get(
        `/tweet-sync/${tokenAddress}/download-tweets`,
        {
          params: {
            start: startDate,
            end: endDate,
          },
        }
      );

      const fileContent = JSON.stringify(response.data.data, null, 2);
      const blob = new Blob([fileContent], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sync_tweets_${tokenAddress}_${new Date().toISOString().replace(/[:.]/g, '-')}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('❌ GET /tweet-sync/:tokenAddress/download-tweets 에러:', error);
      throw new Error(`트윗 다운로드 실패: ${error.response?.data?.message || error.message}`);
    }
  },

  // ===== Author 블랙리스트 관리 =====

  /**
   * Author를 블랙리스트 처리합니다.
   */
  async blacklistAuthor(userName: string): Promise<{ message: string; data: any }> {
    try {
      const response: AxiosResponse<{ message: string; data: any }> = await apiClient.post(
        '/tweet-sync/authors/blacklist',
        { userName }
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ POST /tweet-sync/authors/blacklist 에러:', error);
      if (error.response?.status === 404) {
        throw new Error(`해당 사용자명을 가진 author를 찾을 수 없습니다: ${userName}`);
      }
      throw new Error(`Author 블랙리스트 처리 실패: ${error.response?.data?.message || error.message}`);
    }
  },

  /**
   * 블랙리스트된 Author 목록을 조회합니다.
   */
  async getBlacklistedAuthors(): Promise<{ message: string; data: any[] }> {
    try {
      const response: AxiosResponse<{ message: string; data: any[] }> = await apiClient.get(
        '/tweet-sync/authors/blacklist'
      );
      return response.data;
    } catch (error) {
      console.error('❌ GET /tweet-sync/authors/blacklist 에러:', error);
      throw new Error(`블랙리스트된 author 목록 조회 실패: ${error}`);
    }
  },

  /**
   * Author의 블랙리스트를 해제합니다.
   */
  async unblacklistAuthor(userName: string): Promise<{ message: string; data: any }> {
    try {
      const response: AxiosResponse<{ message: string; data: any }> = await apiClient.post(
        '/tweet-sync/authors/unblacklist',
        { userName }
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ POST /tweet-sync/authors/unblacklist 에러:', error);
      if (error.response?.status === 404) {
        throw new Error(`해당 사용자명을 가진 author를 찾을 수 없습니다: ${userName}`);
      }
      throw new Error(`Author 블랙리스트 해제 실패: ${error.response?.data?.message || error.message}`);
    }
  },

  // ===== 윈도우 트윗/작성자 조회 =====

  /**
   * 특정 윈도우의 트윗과 고유 Author를 조회합니다.
   * @param tokenAddress 토큰 컨트랙트 주소
   * @param timeWindow 시간 단위 윈도우 (1m, 5m, 15m, 30m, 1h, 4h, 12h, 24h)
   * @param highFollowerThreshold High Follower threshold (기본값: 10000)
   * @param windowStartTimestamp 윈도우 시작 시간 (Unix timestamp in milliseconds)
   * @returns 윈도우 내의 트윗과 고유 Author들
   */
  async getWindowTweetsAndAuthors(
    tokenAddress: string,
    timeWindow: '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '12h' | '24h',
    highFollowerThreshold: number = 5000,
    windowStartTimestamp: number,
    windowEndTimestamp?: number
  ): Promise<ApiResponse<WindowTweetsAuthorsResponse>> {
    try {
      const response: AxiosResponse<ApiResponse<WindowTweetsAuthorsResponse>> = await apiClient.post(
        '/tweet-sync/window-tweets-authors',
        {
          tokenAddress,
          timeWindow,
          highFollowerThreshold,
          windowStartTimestamp,
          ...(windowEndTimestamp !== undefined ? { windowEndTimestamp } : {}),
        }
      );
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync-backtest/window-tweets-authors 에러:', error);
      throw new Error(`윈도우 트윗/작성자 조회 실패: ${error}`);
    }
  },

  async getHourlyAdminModCumulative(
    tweetSyncId: string,
    lastCommunitySyncMs: number,
    hours: number = 168,
  ): Promise<ApiResponse<HourlyAdminModCumulativeResponse>> {
    try {
      const response: AxiosResponse<ApiResponse<HourlyAdminModCumulativeResponse>> =
        await apiClient.post('/tweet-sync/hourly-admin-mod-cumulative', {
          tweetSyncId,
          lastCommunitySyncMs,
          hours,
        });
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync/hourly-admin-mod-cumulative 에러:', error);
      throw new Error(`Admin/Mod 시간별 누적 통계 조회 실패: ${error}`);
    }
  },

  // ===== 백테스트용 자주 Quote된 트윗 및 Entity 통계 =====

  /**
   * 백테스트용 자주 Quote된 트윗을 조회합니다.
   * @param tokenAddress 토큰 컨트랙트 주소
   * @param sinceTime 시작 시간 (Unix timestamp in milliseconds, optional)
   * @param untilTime 종료 시간 (Unix timestamp in milliseconds, optional)
   * @param minQuoteCount 최소 Quote 수 (기본값: 3)
   * @param pagination 페이지네이션 옵션
   * @returns 자주 quote된 트윗 목록
   */
  async getBacktestFrequentlyQuotedTweets(
    tokenAddress: string,
    sinceTime?: number,
    untilTime?: number,
    minQuoteCount: number = 3,
    pagination: PaginationDto = { page: 1, limit: 20 }
  ): Promise<FrequentlyQuotedTweetsResponse> {
    try {
      const response: AxiosResponse<FrequentlyQuotedTweetsResponse> = await apiClient.post(
        '/tweet-sync-backtest/frequently-quoted',
        {
          tokenAddress,
          sinceTime,
          untilTime,
          minQuoteCount,
          page: pagination.page || 1,
          limit: pagination.limit || 20,
        }
      );
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync-backtest/frequently-quoted 에러:', error);
      throw new Error(`백테스트 자주 quote된 트윗 조회 실패: ${error}`);
    }
  },

  /**
   * 백테스트용 엔티티 통계를 조회합니다.
   * @param tokenAddress 토큰 컨트랙트 주소
   * @param sinceTime 시작 시간 (Unix timestamp in milliseconds, optional)
   * @param untilTime 종료 시간 (Unix timestamp in milliseconds, optional)
   * @param minCount 최소 언급 횟수 (기본값: 3)
   * @returns 엔티티 통계 데이터
   */
  /**
   * tweet-sync 통계 조회
   * @param tokenAddress 토큰 컨트랙트 주소
   * @param timeWindow 시간 단위 윈도우 (1m, 5m, 15m, 30m, 1h, 4h, 12h, 24h)
   * @param highFollowerThreshold High Follower threshold (기본값: 10000)
   * @param sinceTime 시작 시간 (Unix timestamp in milliseconds, optional)
   * @param untilTime 종료 시간 (Unix timestamp in milliseconds, optional)
   * @returns tweet-sync 통계 데이터
   */
  async getTweetSyncStatistics(
    tokenAddress: string,
    timeWindow: '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '12h' | '24h',
    highFollowerThreshold: number = 5000,
    sinceTime?: number,
    untilTime?: number
  ): Promise<ApiResponse<BacktestStatisticsResponse>> {
    try {
      const requestBody: any = {
        tokenAddress,
        timeWindow,
        highFollowerThreshold,
      };
      
      if (sinceTime !== undefined) {
        requestBody.sinceTime = sinceTime;
      }
      if (untilTime !== undefined) {
        requestBody.untilTime = untilTime;
      }

      const response: AxiosResponse<ApiResponse<BacktestStatisticsResponse>> = await apiClient.post(
        '/tweet-sync/statistics',
        requestBody
      );
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync/statistics 에러:', error);
      throw new Error(`tweet-sync 통계 조회 실패: ${error}`);
    }
  },

  async getBacktestEntityStatistics(
    tokenAddress: string,
    sinceTime?: number,
    untilTime?: number,
    minCount: number = 3
  ): Promise<ApiResponse<EntityStatisticsResponse>> {
    try {
      const response: AxiosResponse<ApiResponse<EntityStatisticsResponse>> = await apiClient.post(
        '/tweet-sync-backtest/entity-statistics',
        {
          tokenAddress,
          sinceTime,
          untilTime,
          minCount,
        }
      );
      return response.data;
    } catch (error) {
      console.error('❌ POST /tweet-sync-backtest/entity-statistics 에러:', error);
      throw new Error(`백테스트 엔티티 통계 조회 실패: ${error}`);
    }
  },
};

export default api;
